import type { Need } from '@oneapp/shared';
import { lazy, type ReactNode, Suspense, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router';
import { Splash } from '../components/Splash';
import { useAuth } from './AuthProvider';
import { clearReturnTo, peekReturnTo, rememberReturnTo } from './returnTo';

// D7: only someone whose account is set to be deleted sees it, so it loads then.
const KeepAccount = lazy(async () => ({ default: (await import('../pages/Privacy')).KeepAccount }));

/** The welcome steps someone still has to finish before using EkApp. */
export type WelcomeStep = 'profile' | 'notice';

const STEP_PATHS: Record<WelcomeStep, string> = { profile: '/welcome/profile', notice: '/welcome/notice' };

/**
 * Which step comes first: a name and the privacy notice the first time (A4, which also asks "18 or older"),
 * or, for someone who joined before, the updated notice (D9). Null when there's nothing to do.
 */
export function stepFor(needs: Need[]): WelcomeStep | null {
  if (needs.includes('profile') || needs.includes('consent')) return 'profile';
  if (needs.includes('notice') || needs.includes('adult')) return 'notice';
  return null;
}

/**
 * Screens for signed-in people. Signed-out visitors go to the welcome screen and come back here
 * afterwards; people who haven't finished a welcome step go to that first.
 *
 * - `step`: this screen is that welcome step.
 * - `beforeNotice`: privacy screens, open before agreeing to an updated notice (D9: "Don't agree? You can
 *   still download your data or delete your account").
 * - `whileDeleting`: open while the account waits to be deleted (downloading the data); every other screen
 *   shows "Keep my account" (D7) then.
 */
export function RequireSignedIn({
  children,
  step,
  beforeNotice = false,
  whileDeleting = false,
}: {
  children: ReactNode;
  step?: WelcomeStep;
  beforeNotice?: boolean;
  whileDeleting?: boolean;
}) {
  const { state } = useAuth();
  const location = useLocation();
  const here = `${location.pathname}${location.search}`;
  const signedIn = state.status === 'signedIn';

  useEffect(() => {
    if (signedIn && peekReturnTo() === here) clearReturnTo();
  }, [signedIn, here]);

  if (state.status === 'loading') return <Splash />;
  if (state.status === 'error') return <Splash onRetry={state.retry} reason={state.reason} />;
  if (state.status !== 'signedIn') {
    rememberReturnTo(here);
    return <Navigate to="/welcome" replace />;
  }
  if (state.needs.includes('keep')) {
    if (whileDeleting) return <>{children}</>;
    return (
      <Suspense fallback={<Splash />}>
        <KeepAccount />
      </Suspense>
    );
  }
  const next = stepFor(state.needs);
  if (step) {
    if (next === step) return <>{children}</>;
    return <Navigate to={next ? STEP_PATHS[next] : (peekReturnTo() ?? '/')} replace />;
  }
  if (next && !(beforeNotice && next === 'notice')) return <Navigate to={STEP_PATHS[next]} replace />;
  return <>{children}</>;
}

/** Welcome and sign-in screens: someone already signed in goes on to the app. */
export function RequireSignedOut({ children }: { children: ReactNode }) {
  const { state } = useAuth();
  if (state.status === 'loading') return <Splash />;
  if (state.status === 'error') return <Splash onRetry={state.retry} reason={state.reason} />;
  if (state.status === 'signedIn') {
    const next = stepFor(state.needs);
    return <Navigate to={next ? STEP_PATHS[next] : (peekReturnTo() ?? '/')} replace />;
  }
  return <>{children}</>;
}
